import { useEffect, useState } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from "@tanstack/react-router";
import { SearchPalette } from "./components/SearchPalette";
import { Sidebar } from "./components/Sidebar";
import { OnboardingGuard } from "./components/OnboardingGuard";
import { OverviewRoute } from "./routes/Overview";
import { DomainListRoute } from "./routes/DomainList";
import { DomainDetailRoute } from "./routes/Domain";
import { IpDetailRoute } from "./routes/IpDetail";
import { QuarantineRoute } from "./routes/Quarantine";
import {
  AdminAlertsRoute,
  AdminLayoutRoute,
  AdminOperationsRoute,
  AdminUsersRoute,
} from "./routes/Admin";
import { AdminUsageRoute } from "./routes/AdminUsage";

// /preview and /validator pull in the heaviest deps in the bundle —
// parser (fflate + fast-xml-parser) and validator (DoH + parsers) — and
// most users never visit them. Lazy-loading keeps them out of the
// initial chunk, splitting the bundle along its natural seams.
const LazyPreviewRoute = lazyRouteComponent(
  () => import("./routes/Preview"),
  "PreviewRoute",
);
const LazyValidatorRoute = lazyRouteComponent(
  () => import("./routes/Validator"),
  "ValidatorRoute",
);
const LazyDnsMonitorRoute = lazyRouteComponent(
  () => import("./routes/DnsMonitor"),
  "DnsMonitorRoute",
);
const LazyDnsMonitorDomainRoute = lazyRouteComponent(
  () => import("./routes/DnsMonitorDomain"),
  "DnsMonitorDomainRoute",
);
const LazyPortfolioRoute = lazyRouteComponent(
  () => import("./routes/Portfolio"),
  "PortfolioRoute",
);
const LazyAlertsRoute = lazyRouteComponent(
  () => import("./routes/Alerts"),
  "AlertsRoute",
);
const LazyOnboardingRoute = lazyRouteComponent(
  () => import("./routes/Onboarding"),
  "OnboardingRoute",
);
const LazySettingsRoute = lazyRouteComponent(
  () => import("./routes/Settings"),
  "SettingsRoute",
);
const LazyPlatformRoute = lazyRouteComponent(
  () => import("./routes/Platform"),
  "PlatformRoute",
);

// Root layout owns the search palette state. cmd/ctrl+K toggles the
// modal regardless of which route is active. Sidebar gets a callback
// so its "Search" button opens the same palette.
function RootLayout() {
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Cmd+K (mac) / Ctrl+K (win/linux). Use `metaKey || ctrlKey` so
      // a single binding covers both. preventDefault stops the
      // browser's location-bar focus on Ctrl+K in some shells.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <Sidebar onOpenSearch={() => setSearchOpen(true)} />
      <main className="flex-1 overflow-x-auto px-6 py-8">
        <div className="mx-auto max-w-[96rem]">
          <OnboardingGuard>
            <Outlet />
          </OnboardingGuard>
        </div>
      </main>
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: OverviewRoute,
});

const domainListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/domains",
  component: DomainListRoute,
});

const domainDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/domains/$domain",
  component: DomainDetailRoute,
  // `subdomain` drills the Sources + volume panels down to one header_from.
  validateSearch: (s: Record<string, unknown>): { subdomain?: string } => ({
    subdomain: typeof s.subdomain === "string" && s.subdomain ? s.subdomain : undefined,
  }),
});

const ipDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ips/$ip",
  component: IpDetailRoute,
});

const quarantineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quarantine",
  component: QuarantineRoute,
});

const previewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/preview",
  component: LazyPreviewRoute,
  validateSearch: (s: Record<string, unknown>): { reportId?: number } => {
    const raw = s.reportId;
    const num = typeof raw === "string" ? Number(raw) : typeof raw === "number" ? raw : NaN;
    return Number.isFinite(num) && num > 0 ? { reportId: num } : {};
  },
});

const validatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/validator",
  component: LazyValidatorRoute,
  validateSearch: (s: Record<string, unknown>): { domain?: string } => ({
    domain: typeof s.domain === "string" ? s.domain : undefined,
  }),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminLayoutRoute,
});

// Index match — `/admin` lands directly on the Users section without
// a redirect (so the URL bar shows /admin instead of /admin/users when
// the operator clicks the sidebar link).
const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: AdminUsersRoute,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/users",
  component: AdminUsersRoute,
});

const adminAlertsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/alerts",
  component: AdminAlertsRoute,
});

const adminOperationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/operations",
  component: AdminOperationsRoute,
});

const adminUsageRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/usage",
  component: AdminUsageRoute,
});

const dnsMonitorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dns-monitor",
  component: LazyDnsMonitorRoute,
});

const dnsMonitorDomainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dns-monitor/$domain",
  component: LazyDnsMonitorDomainRoute,
});

const portfolioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portfolio",
  component: LazyPortfolioRoute,
});

const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/alerts",
  component: LazyAlertsRoute,
});

// v3 / R1 — self-service onboarding. Lazy-loaded because most users
// never see it (existing members get redirected away by the wizard
// itself, new signups land here once per workspace).
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: LazyOnboardingRoute,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: LazySettingsRoute,
});

const platformRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/platform",
  component: LazyPlatformRoute,
});

const routeTree = rootRoute.addChildren([
  overviewRoute,
  domainListRoute,
  domainDetailRoute,
  ipDetailRoute,
  quarantineRoute,
  previewRoute,
  validatorRoute,
  adminRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminAlertsRoute,
    adminOperationsRoute,
    adminUsageRoute,
  ]),
  dnsMonitorRoute,
  dnsMonitorDomainRoute,
  portfolioRoute,
  alertsRoute,
  onboardingRoute,
  settingsRoute,
  platformRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
